<!--
  @component PerformancePanel — performance: metrics + tools.
  tier:      screen content (inside CacheIO + QueueIO mounts)
  scope:     how the app performs (real Fly telemetry as charts) and
             what tunes it (cache + queue — provision + status). The
             full cache/queue operation console is a future product.
  reads:     useDeploymentIO  → telemetry.usage (REAL) + cache/queue.list
             useCacheIO/useQueueIO → provision (REAL metadata)
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useCacheIO } from "../../../../global/io/nodes/deployment/CacheIO.svelte";
  import { useQueueIO } from "../../../../global/io/nodes/deployment/QueueIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import StatusNote from "../../../../global/visuals/primitives/StatusNote.svelte";
  import Sparkline from "../../../../global/visuals/primitives/Sparkline.svelte";
  import Skeleton from "../../../../global/visuals/primitives/Skeleton.svelte";

  const identity = useIdentityIO();
  const cache = useCacheIO();
  const queue = useQueueIO();
  const node = useDeploymentIO();

  const project = $derived(identity.currentProject);

  // ── Telemetry (REAL, polled + accumulated) ──────────────────────
  interface Usage {
    appExists: boolean;
    compute: { machineCount: number; runningCount: number; memoryAllocatedMb: number };
    metrics?: { memoryUsedMb: number | null; networkInBytesPerSec: number | null; networkOutBytesPerSec: number | null; cpuPercent: number | null; rps: number | null };
  }
  let usage = $state<Usage | null>(null);
  let loading = $state(true);
  let series = $state<{ cpu: number[]; mem: number[]; rps: number[]; netIn: number[]; netOut: number[] }>({ cpu: [], mem: [], rps: [], netIn: [], netOut: [] });
  let retry: ReturnType<typeof setTimeout> | null = null;

  function push(u: Usage): void {
    const cap = (a: number[], v: number) => [...a, v].slice(-32);
    series = {
      cpu: cap(series.cpu, u.metrics?.cpuPercent ?? 0),
      mem: cap(series.mem, u.metrics?.memoryUsedMb ?? 0),
      rps: cap(series.rps, u.metrics?.rps ?? 0),
      netIn: cap(series.netIn, u.metrics?.networkInBytesPerSec ?? 0),
      netOut: cap(series.netOut, u.metrics?.networkOutBytesPerSec ?? 0),
    };
  }
  async function refresh(): Promise<void> {
    if (!project) { loading = false; return; }
    try { const u = (await node.dispatch("telemetry.usage", { target: { id: project.id } })) as Usage; usage = u; loading = false; push(u); }
    catch (e) { loading = false; if (e instanceof Error && e.message === "transport not connected" && !retry) retry = setTimeout(() => { retry = null; void refresh(); }, 1000); }
  }
  $effect(() => {
    if (!project) return;
    loading = true; void refresh();
    const iv = setInterval(() => void refresh(), 8000);
    return () => { clearInterval(iv); if (retry) { clearTimeout(retry); retry = null; } };
  });

  // ── Cache + queue allocations (REAL metadata) ───────────────────
  interface Allocation { id: string; namespace: string; status: "provisioning" | "ready" | "failed" | "destroying" | "destroyed"; }
  let caches = $state<Allocation[]>([]);
  let queues = $state<Allocation[]>([]);
  let cacheBusy = $state(false);
  let queueBusy = $state(false);
  const activeCache = $derived(caches.find((c) => c.status === "ready" || c.status === "provisioning") ?? null);
  const activeQueue = $derived(queues.find((q) => q.status === "ready" || q.status === "provisioning") ?? null);

  async function refreshAlloc(): Promise<void> {
    if (!project) return;
    const target = { id: project.id };
    try { caches = ((await node.dispatch("cache.list", { target })) as Allocation[]) ?? []; } catch { caches = []; }
    try { queues = ((await node.dispatch("queue.list", { target })) as Allocation[]) ?? []; } catch { queues = []; }
  }
  $effect(() => { if (project) void refreshAlloc(); });

  async function provisionCache(): Promise<void> {
    if (!project) return; cacheBusy = true;
    try { await cache.provision({ id: project.id }); } catch { /* surfaced */ }
    finally { cacheBusy = false; void refreshAlloc(); }
  }
  async function provisionQueue(): Promise<void> {
    if (!project) return; queueBusy = true;
    try { await queue.provision({ id: project.id }); } catch { /* surfaced */ }
    finally { queueBusy = false; void refreshAlloc(); }
  }

  function fmt(n: number | null | undefined, unit = "", d = 0): string { return n === null || n === undefined ? "—" : `${n.toFixed(d)}${unit}`; }
  function fmtBytes(b: number | null | undefined): string {
    if (b === null || b === undefined) return "—";
    if (b < 1024) return `${b.toFixed(0)} B/s`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB/s`;
    return `${(b / 1024 / 1024).toFixed(2)} MB/s`;
  }
</script>

<div class="screen">
  <header class="head">
    <h1>Performance</h1>
    <p class="sub">How your app is performing, and the tools that make it faster.</p>
  </header>

  <!-- Metrics charts (real Fly telemetry) -->
  <Card eyebrow="Live · last few minutes" title="Metrics">
    {#if loading}
      <div class="charts">
        {#each Array.from({ length: 4 }) as _, i (i)}
          <div class="chart"><Skeleton variant="line" width="40%" height="11px" /><Skeleton variant="line" width="55%" height="22px" /><Skeleton variant="block" height="48px" /></div>
        {/each}
      </div>
    {:else}
      <div class="charts">
        <div class="chart">
          <span class="c-label">CPU</span>
          <span class="c-value">{fmt(usage?.metrics?.cpuPercent, "%")}</span>
          <Sparkline values={series.cpu} color="var(--text-secondary)" width={240} height={48} />
        </div>
        <div class="chart">
          <span class="c-label">Memory</span>
          <span class="c-value">{fmt(usage?.metrics?.memoryUsedMb, " MB")}<span class="c-sub"> / {usage?.compute.memoryAllocatedMb ?? 0} MB</span></span>
          <Sparkline values={series.mem} color="var(--text-secondary)" width={240} height={48} />
        </div>
        <div class="chart">
          <span class="c-label">Requests / sec</span>
          <span class="c-value">{fmt(usage?.metrics?.rps, "", 1)}</span>
          <Sparkline values={series.rps} color="var(--text-secondary)" width={240} height={48} />
        </div>
        <div class="chart">
          <span class="c-label">Bandwidth</span>
          <span class="c-value c-band">↓ {fmtBytes(usage?.metrics?.networkInBytesPerSec)} · ↑ {fmtBytes(usage?.metrics?.networkOutBytesPerSec)}</span>
          <Sparkline values={series.netOut} color="var(--text-secondary)" width={240} height={48} />
        </div>
      </div>
      <p class="machines">{usage?.compute.runningCount ?? 0} of {usage?.compute.machineCount ?? 0} machines running · charts build up as your app serves traffic.</p>
    {/if}
  </Card>

  <!-- Performance tools -->
  <div class="tools">
    <Card eyebrow="Tool" title="Cache">
      {#if !activeCache}
        <p class="tool-help">Speed up your app with a managed cache layer.</p>
        <button type="button" class="btn-primary" disabled={cacheBusy} onclick={() => void provisionCache()}>{cacheBusy ? "Setting up…" : "Set up cache"}</button>
      {:else}
        <div class="tool-status"><span class="dot {activeCache.status === 'ready' ? 'live' : ''}"></span><span>{activeCache.status === "ready" ? "Provisioned" : "Setting up…"} · {activeCache.namespace}</span></div>
        <StatusNote tone="pending" message="Warm, invalidate, and flush controls turn on once your deploy connection is live." />
      {/if}
    </Card>

    <Card eyebrow="Tool" title="Queue">
      {#if !activeQueue}
        <p class="tool-help">Run background jobs and decouple work with a managed queue.</p>
        <button type="button" class="btn-primary" disabled={queueBusy} onclick={() => void provisionQueue()}>{queueBusy ? "Setting up…" : "Set up queue"}</button>
      {:else}
        <div class="tool-status"><span class="dot {activeQueue.status === 'ready' ? 'live' : ''}"></span><span>{activeQueue.status === "ready" ? "Provisioned" : "Setting up…"} · {activeQueue.namespace}</span></div>
        <StatusNote tone="pending" message="Publish, peek, and purge controls turn on once your deploy connection is live." />
      {/if}
    </Card>
  </div>
</div>

<style>
  .screen { display: flex; flex-direction: column; gap: var(--space-5); max-width: 1100px; font-family: var(--type-font); }
  .head h1 { margin: 0 0 var(--space-2); font-size: 24px; font-weight: 600; letter-spacing: -0.025em; color: var(--text-primary); }
  .sub { margin: 0; font-size: 13.5px; color: var(--text-secondary); }

  .charts { display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--space-5); }
  .chart { display: flex; flex-direction: column; gap: var(--space-1); }
  .c-label { font-size: 11.5px; color: var(--text-tertiary); }
  .c-value { font-size: 21px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .c-sub { font-size: 13px; font-weight: 400; color: var(--text-tertiary); }
  .c-band { font-size: 15px; }
  .machines { margin: var(--space-4) 0 0; padding-top: var(--space-3); border-top: 0.5px solid var(--border-subtle); font-size: 12px; color: var(--text-tertiary); }

  .tools { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4); }
  .tool-help { margin: 0 0 var(--space-3); font-size: 13px; color: var(--text-secondary); line-height: 1.6; }
  .tool-status { display: inline-flex; align-items: center; gap: var(--space-2); font-size: 13px; color: var(--text-secondary); margin-bottom: var(--space-3); }
  .dot { width: 6px; height: 6px; border-radius: var(--radius-pill); background: var(--text-tertiary); }
  .dot.live { background: var(--success); }

  .btn-primary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 36px; background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .btn-primary:hover:not(:disabled) { background: var(--bg-selected); border-color: var(--border-focus); }
  .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

  @media (max-width: 880px) { .charts { grid-template-columns: 1fr; } .tools { grid-template-columns: 1fr; } }
</style>
